import { useEffect, useState } from 'react'
import type { MaintenanceNotice } from '../api/types'
import { Button } from './ui'
import { BrandMark, Wordmark } from './Brand'

/**
 * THE CLOSED SIGN.
 *
 * Two screens, one idea: when something is in maintenance, it is REPLACED, not
 * annotated.
 *
 * ---------------------------------------------------------------------------
 * WHAT THIS REPLACED, AND WHY
 *
 * Until 17 Sep 2026 a maintenance window changed nothing about where a person
 * could go. The platform signed them in, drew the sidebar, offered every report
 * they were entitled to, and then printed a small amber panel in the content
 * area of whichever one they opened — once per click, screen after screen.
 *
 * That is the experience of a building with the lights on, the doors unlocked
 * and a note taped inside each office. Everybody who met it concluded the
 * product was broken, because a closed thing that still invites you in is
 * indistinguishable from a broken one. Worse, the note was the same size as an
 * ordinary empty-state, so it read as "no data" rather than "not now".
 *
 * A closed platform is now a closed platform: the shell is never drawn, and the
 * user is not inside it. A closed report is a closed report: the shell stays,
 * because the reports beside it are still working and walking to one of them is
 * the useful thing to do next.
 *
 * ---------------------------------------------------------------------------
 * MOTION, AND WHERE IT IS ALLOWED
 *
 * styles.css is explicit that the entrance animations belong to sign-in and
 * nowhere else, because movement competes with figures. The split here follows
 * that rule exactly rather than bending it:
 *
 *   * the PLATFORM screen is not inside the platform — it is a full-viewport
 *     brand surface, the same category of screen as sign-in, and it arrives
 *     with the same gesture;
 *   * the REPORT screen IS inside the platform, sitting where a report's
 *     figures normally are, and it is completely still.
 *
 * ---------------------------------------------------------------------------
 * NOT A CONTROL
 *
 * MaintenanceMiddleware refuses the data whatever this component renders.
 * Deleting it in a debugger produces an empty shell full of 503s, not access.
 */

/** How often a closed screen quietly asks whether it can stop being closed. */
const RECHECK_MS = 30_000

/**
 * Re-renders on a slow tick, and asks the caller to re-read the platform's state.
 *
 * Both halves matter and they are different. The tick keeps "expected back in
 * about 2 hr" from freezing at whatever it said when the screen mounted — a
 * countdown that does not count is worse than no countdown, because it looks
 * live. The callback is what actually ends the outage for this tab: a window
 * lifted at 14:05 should not need somebody to think of pressing reload.
 *
 * Deliberately 30 seconds and deliberately not exponential. At 2,000 users a
 * closed platform is 2,000 tabs on this timer, which is ~66 requests a second
 * against /api/v1/me — a route that answers from a 10-second cache and touches
 * no report data. Backing off would save little and would mean the first person
 * to notice the platform is back is nobody.
 */
function useRecheck(onRecheck: () => void) {
  const [, setTick] = useState(0)

  useEffect(() => {
    const id = setInterval(() => {
      setTick((t) => t + 1)
      onRecheck()
    }, RECHECK_MS)
    return () => clearInterval(id)
  }, [onRecheck])
}

/**
 * "about 2 hr", or nothing.
 *
 * Deliberately vaguer than the timestamp we hold. ExpectedEndUtc is an
 * operator's estimate written when they opened the window, and rendering it as
 * "16:45" would promise a precision nobody has — then be wrong at 16:46 in a way
 * a user can time. "About" is the honest word for an estimate, and it is the one
 * relativeTime's own coarse buckets are already built for.
 */
function expectedBack(iso: string): string | null {
  if (!iso) return null
  const ms = new Date(iso).getTime() - Date.now()
  if (Number.isNaN(ms)) return null
  // Past its estimate and still closed. This happens — the window is bounded at
  // 7 days, not at whatever was guessed — and saying "expected back in 0 min"
  // for an hour is how a screen loses a reader's trust.
  if (ms <= 0) return null

  const mins = Math.round(ms / 60_000)
  if (mins < 60) return `about ${mins} ${mins === 1 ? 'minute' : 'minutes'}`
  const hours = Math.round(mins / 60)
  if (hours < 24) return `about ${hours} ${hours === 1 ? 'hour' : 'hours'}`
  const days = Math.round(hours / 24)
  return `about ${days} ${days === 1 ? 'day' : 'days'}`
}

/* ===========================================================================
   PLATFORM SCOPE — the whole viewport, and the shell is never drawn.
   =========================================================================== */

/**
 * The platform is closed. This is the entire screen.
 *
 * Navy full-bleed rather than a card on the usual grey, and that is the load-
 * bearing decision. A white card on --color-surface-2 is what every page in the
 * product looks like, so it would read as a page IN the platform reporting a
 * problem. The navy field is the same surface sign-in uses and carries the same
 * message: you are not inside yet.
 */
export function PlatformClosed({
  notice,
  user,
  onRecheck,
  onSignOut,
  viewAs,
  onExitViewAs,
}: {
  notice: MaintenanceNotice
  /** Who is signed in. Shown so nobody wonders whether the block is about them. */
  user: string | null
  onRecheck: () => void
  onSignOut: () => void
  /** Whose permissions are being borrowed, if any. */
  viewAs: string | null
  onExitViewAs: () => void
}) {
  useRecheck(onRecheck)
  const back = expectedBack(notice.expectedEndUtc)

  return (
    <div className="on-navy relative grid h-dvh place-items-center overflow-y-auto bg-(--color-navy-800) px-6 py-10 text-white">
      <Backdrop />

      <main className="relative w-full max-w-xl">
        <div className="anim-rise flex items-center gap-3">
          <BrandMark size={34} tone="white" animate />
          <Wordmark tone="white" subtitle="" />
        </div>

        {/* The state, named before it is described. Amber because that is what
            --color-warn means everywhere else in the product; lifted to
            --color-warn-on-navy because the light-ground value is invisible
            here. The dot is the same object the Maintenance console draws
            beside an open window, so an operator sees their own indicator. */}
        <div
          className="anim-rise mt-10 flex items-center gap-2.5"
          style={{ animationDelay: '80ms' }}
        >
          <span
            aria-hidden
            className="inline-block h-2 w-2 shrink-0 rounded-full bg-(--color-warn-on-navy) ring-4 ring-(--color-warn-on-navy)/20"
          />
          <span className="text-(length:--text-micro) font-semibold uppercase tracking-[0.12em] text-(--color-warn-on-navy)">
            Maintenance
          </span>
        </div>

        <h1
          className="anim-rise mt-4 text-[38px] font-semibold leading-[1.1] tracking-[-0.025em] text-white"
          style={{ animationDelay: '120ms' }}
        >
          MBI is closed
          <br />
          <span className="text-white/55">for the moment.</span>
        </h1>

        <div
          aria-hidden
          className="anim-draw mt-6 h-[3px] w-24 rounded-full bg-(--color-warn-on-navy)/70"
          style={{ animationDelay: '380ms' }}
        />

        {/* The operator's own words. Kept at full strength and given its own
            measure — it is the one line on this screen written by a person who
            knows what is happening, and everything else is scaffolding. */}
        <p
          className="anim-rise mt-7 max-w-lg text-[17px] font-light leading-relaxed text-white/80"
          style={{ animationDelay: '200ms' }}
        >
          {notice.message}
        </p>

        <div
          className="anim-rise mt-8 flex flex-wrap items-center gap-x-4 gap-y-3"
          style={{ animationDelay: '260ms' }}
        >
          {/* A white button on navy: the one action, and the only full-strength
              element below the headline. */}
          <button
            type="button"
            onClick={onRecheck}
            className="inline-flex h-10 items-center justify-center gap-2 rounded border border-transparent bg-white px-5 text-(length:--text-base) font-medium text-(--color-navy-800) transition-all duration-150 hover:-translate-y-px hover:bg-white/90 active:translate-y-0"
          >
            Check again
          </button>

          <p aria-live="polite" className="text-(length:--text-base) text-white/55">
            {back ? `Expected back in ${back}.` : 'Expected back shortly.'}{' '}
            <span className="text-white/40">Checking every 30 seconds.</span>
          </p>
        </div>

        {/* THE WAY OUT.
            An administrator who was acting as somebody before the window opened is
            being shown this screen on purpose — they asked to be treated as
            somebody who is not an admin. Without this button they would be
            treated as that person with no way to stop, on a screen that has no
            navigation, and the only remedy would be reloading the tab. */}
        {viewAs && (
          <div
            className="anim-rise mt-9 rounded-md border border-(--color-privileged)/45 bg-(--color-privileged)/15 px-4 py-3.5"
            style={{ animationDelay: '320ms' }}
          >
            <p className="text-(length:--text-body) leading-relaxed text-white/80">
              You are signed in as <span className="font-semibold text-white">{viewAs}</span>, so you
              are being shown their experience of this window rather than your own.
            </p>
            <button
              type="button"
              onClick={onExitViewAs}
              className="mt-2.5 rounded text-(length:--text-body) font-semibold text-white underline underline-offset-4 hover:text-white/80"
            >
              Stop sign in as
            </button>
          </div>
        )}

        <div
          className="anim-rise mt-10 flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-white/12 pt-5 text-(length:--text-small) text-white/40"
          style={{ animationDelay: '380ms' }}
        >
          {user && (
            /* Named so nobody reads a platform-wide window as a problem with
               their own account — the two are told apart by nothing else on this
               screen, and "is it just me?" is the first thing anyone asks.

               The separator lives INSIDE this span rather than beside it: as its
               own flex child it becomes a wrap opportunity, and at 390px it
               ended up alone at the start of the last line, reading as a bullet
               point in front of Sign out. */
            <span>
              Signed in as <span className="text-white/65">{user}</span>. This applies to everyone.
              <span aria-hidden className="text-white/20">
                {' · '}
              </span>
            </span>
          )}
          <button
            type="button"
            onClick={onSignOut}
            className="rounded underline underline-offset-4 hover:text-white/70"
          >
            Sign out
          </button>
        </div>
      </main>
    </div>
  )
}

/**
 * The ambient field behind the takeover.
 *
 * The ledger grid from sign-in, and one glow rather than two — STATIC, where
 * sign-in's drift. The drift is a welcome; it is the wrong gesture for a screen
 * that exists to say nothing is happening right now. Shared with "Access denied"
 * (AccessDenied.tsx), the other takeover drawn before the Shell.
 */
export function Backdrop() {
  return (
    <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
      <div
        className="absolute inset-0 opacity-[0.06]"
        style={{
          backgroundImage:
            'linear-gradient(to right, #fff 1px, transparent 1px), linear-gradient(to bottom, #fff 1px, transparent 1px)',
          backgroundSize: '56px 56px',
        }}
      />
      <div className="absolute -right-40 -top-48 h-[560px] w-[560px] rounded-full bg-(--color-navy-500)/30 blur-[120px]" />
      <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-(--color-navy-900)/60 to-transparent" />
    </div>
  )
}

/* ===========================================================================
   REPORT SCOPE — the page area, inside the shell that still works.
   =========================================================================== */

/**
 * One report is closed. Rendered where that report's screens would be.
 *
 * The shell is deliberately KEPT. A report-scoped window says nothing about the
 * rest of the platform, and taking the navigation away would tell the reader the
 * opposite — that everything is down — while removing the one thing that is
 * actually useful to them, which is the link to a report that is working.
 *
 * Still. No entrance animation: this sits in the data canvas, and styles.css
 * reserves motion for the sign-in surface.
 */
export function ReportClosed({
  notice,
  reportName,
  otherReportsOpen,
  onRecheck,
}: {
  notice: MaintenanceNotice
  /** The report's display name, as the sidebar spells it. */
  reportName: string
  /** Whether this identity has somewhere else to go. Decides one sentence. */
  otherReportsOpen: boolean
  onRecheck: () => void
}) {
  useRecheck(onRecheck)
  const back = expectedBack(notice.expectedEndUtc)

  return (
    /* Centred in the page area rather than pinned to the top. A top-aligned
       block of prose where a dense report normally starts reads as a report
       that failed to load most of itself. */
    <div className="grid h-full place-items-center px-6 py-10">
      <main className="w-full max-w-lg">
        <div className="overflow-hidden rounded-xl border border-(--color-line) bg-(--color-surface) shadow-(--shadow-card)">
          {/* A band of the state's colour across the top, the same device the
              sign-in card uses in brand navy. It gives the card a front, and it
              is the first thing read at a glance. */}
          <div aria-hidden className="h-[3px] w-full bg-(--color-warn)" />

          <div className="px-7 pb-7 pt-6">
            <div className="flex items-center gap-2.5">
              <span
                aria-hidden
                className="inline-block h-2 w-2 shrink-0 rounded-full bg-(--color-warn) ring-4 ring-(--color-warn)/15"
              />
              <span className="text-(length:--text-micro) font-semibold uppercase tracking-[0.1em] text-(--color-warn)">
                Maintenance
              </span>
            </div>

            <h1 className="mt-4 text-(length:--text-2xl) font-semibold leading-tight tracking-[-0.02em] text-(--color-ink)">
              {reportName} is closed
            </h1>

            {/* The operator's message, at body weight and full contrast. */}
            <p className="mt-3 text-(length:--text-base) leading-relaxed text-(--color-ink-2)">
              {notice.message}
            </p>

            {/* A two-column grid, not a row of flex pairs: the labels are
                different lengths, so laying them out inline leaves the values
                starting at two different x positions and the block reads as two
                unrelated sentences rather than one small table. auto-width on
                the first column keeps it tight to the longer label. */}
            <dl className="mt-5 grid grid-cols-[auto_1fr] gap-x-5 gap-y-1.5 border-t border-(--color-line) pt-4 text-(length:--text-body) leading-relaxed">
              <dt className="text-(--color-ink-3)">Expected back</dt>
              <dd aria-live="polite" className="text-(--color-ink-2)">
                {back ? `in ${back}` : 'shortly'}
              </dd>
              {otherReportsOpen && (
                <>
                  <dt className="text-(--color-ink-3)">Everything else</dt>
                  {/* Said explicitly because the alternative reading — "the whole
                      platform is down" — is the one people default to, and it
                      sends them away from reports that are working. */}
                  <dd className="text-(--color-ink-2)">Your other reports are unaffected.</dd>
                </>
              )}
            </dl>

            <div className="mt-6 flex flex-wrap items-center gap-3">
              <Button variant="primary" onClick={onRecheck}>
                Check again
              </Button>
              <span className="text-(length:--text-small) text-(--color-ink-3)">
                Checking every 30 seconds.
              </span>
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}

/**
 * The strip shown when a window is open but reads are still being served.
 *
 * Read grace is an operator's deliberate choice — the "Keep serving reads" box —
 * and it means the API is answering on purpose. Replacing the report would
 * contradict a server that is working, so this is the one case that stays an
 * annotation: the report renders, with the reason it might change under them.
 */
export function ReadGraceBanner({ notice, reportName }: { notice: MaintenanceNotice; reportName: string }) {
  const back = expectedBack(notice.expectedEndUtc)

  return (
    /* Full-bleed, and a bottom rule rather than a box. It sits directly under
       the demo and View As bars, which both run edge to edge; an inset rounded
       card there reads as a floating panel that has slipped above the
       navigation rather than as another band of chrome. */
    <div
      role="status"
      className="flex flex-wrap items-center gap-x-2 gap-y-1 border-b border-(--color-warn)/30 bg-(--color-warn)/8 px-4 py-2.5 text-(length:--text-body) leading-relaxed text-(--color-ink-2)"
    >
      <span
        aria-hidden
        className="inline-block h-2 w-2 shrink-0 rounded-full bg-(--color-warn) ring-4 ring-(--color-warn)/15"
      />
      <span className="font-semibold text-(--color-warn)">
        {notice.scope === 'platform' ? 'Maintenance in progress' : `${reportName} is in maintenance`}
      </span>
      <span>{notice.message}</span>
      <span className="text-(--color-ink-3)">
        Figures are still being served{back ? `, for ${back}` : ''}; anything that saves is refused.
      </span>
    </div>
  )
}
